import * as React from 'react';

import { Button, CircularProgress, Typography } from '@mui/material';
import {
  Check,
  Copy,
  Download,
  Eye,
  FolderCheck,
  ImageOff,
  Palette,
  WandSparkles,
} from 'lucide-react';

import { type ToolPart } from '@modules/_shared/components/chat/ApprovalActions';
import { type ArtifactSummary } from '@modules/_shared/components/chat/artifactFiles';
import {
  chatApiBase,
  chatFetch,
  getChatHeaders,
} from '@modules/_shared/components/chat/chatFetch';
import { ChatImagePreviewDialog } from '@modules/_shared/components/chat/ChatImagePreviewDialog';
import {
  useChatFile,
  useFileStateText,
} from '@modules/_shared/components/chat/SavedFileCard';
import {
  GENERATE_IMAGE_TOOL_NAME,
  type VisualImage,
  canCopyImages,
  describeError,
  downloadUrlOf,
  fetchVisualBytes,
  formatImageSize,
  parseVisualToolOutput,
  savedVisualArtifactOf,
  thumbnailSizeOf,
  visualBytesUrlOf,
  visualFilenameOf,
  visualTitleOf,
} from '@modules/_shared/components/chat/visualToolOutput';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

// Card for the crm-chat-api `render_visual` / `generate_image` tools: the
// generated image(s) with preview, download and copy. Only the signed URLs of
// the tool output (and the chat's own visuals route, for Copy) are loaded —
// never anything from the tool input. Only calls that produced an image get
// here; running and failed ones are steps (messageSegments.ts groupParts).

type Colors = ReturnType<typeof useTheme>['colors'];

const THUMBNAIL_MAX = { width: 260, height: 320 };
const COPIED_FEEDBACK_MS = 2000;

const errorPreCss = (colors: Colors) =>
  ({
    margin: 0,
    padding: '8px 10px',
    backgroundColor: colors.grey100,
    color: colors.errorText,
    borderRadius: 6,
    fontSize: '0.75rem',
    lineHeight: 1.5,
    fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: 220,
    overflow: 'auto',
  } as const);

// Chrome only accepts image/png on the clipboard; generated images may be
// JPEG/WebP, so those are re-encoded through a canvas first.
const toPngBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type === 'image/png') {
    return blob;
  }
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext('2d');
  if (context == null) {
    bitmap.close();
    throw new Error('Canvas 2D context is not available');
  }
  context.drawImage(bitmap, 0, 0);
  bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      png =>
        png != null
          ? resolve(png)
          : reject(new Error('Could not encode the image as PNG')),
      'image/png',
    ),
  );
};

// The signed GCS URL has no CORS for the CRM origin, so Copy reads the bytes
// from crm-chat-api with the chat's Bearer auth instead.
const fetchPngBlob = async (chatId: string, imageId: string): Promise<Blob> =>
  toPngBlob(
    await fetchVisualBytes({
      url: visualBytesUrlOf(chatApiBase, chatId, imageId),
      headers: getChatHeaders(),
      fetchImpl: chatFetch,
    }),
  );

// The persisted chat the cards and attachments belong to; null for a chat that
// has no messages on the server yet (Copy and attachment Download are then
// hidden). Provided by ChatPanelInner.
const VisualChatIdContext = React.createContext<string | null>(null);

export const VisualChatIdProvider = VisualChatIdContext.Provider;

export const useVisualChatId = () => React.useContext(VisualChatIdContext);

type CopyState =
  | { kind: 'idle' }
  | { kind: 'copying' }
  | { kind: 'copied' }
  | { kind: 'error'; message: string };

const ExpiredChip = ({ colors }: { colors: Colors }) => {
  const { t } = useLocale();
  return (
    <div
      role="alert"
      css={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        borderRadius: 8,
        border: `1px solid ${colors.red100}`,
        backgroundColor: colors.red50,
        color: colors.errorText,
        fontSize: '0.75rem',
        lineHeight: 1.4,
      }}
    >
      <ImageOff size={14} style={{ flexShrink: 0 }} />
      <span>{t('Image link expired or unavailable — reload the chat')}</span>
    </div>
  );
};

const VisualImageTile = ({
  image,
  alt,
  onPreview,
}: {
  image: VisualImage;
  alt: string;
  onPreview: (url: string) => void;
}) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const chatId = React.useContext(VisualChatIdContext);
  // An expired signed URL fails to load once the chat has been open ~1h.
  const [broken, setBroken] = React.useState(false);
  const [copy, setCopy] = React.useState<CopyState>({ kind: 'idle' });
  const resetTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (resetTimer.current != null) {
        clearTimeout(resetTimer.current);
      }
    },
    [],
  );

  const url = broken ? null : image.url;
  const href = broken ? null : downloadUrlOf(image);
  const size = formatImageSize(image);
  const thumbnail = thumbnailSizeOf(image, THUMBNAIL_MAX);

  const copyImage = async () => {
    if (chatId == null) {
      return;
    }
    setCopy({ kind: 'copying' });
    try {
      // The blob is passed as a promise so Safari keeps the user gesture.
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': fetchPngBlob(chatId, image.imageId),
        }),
      ]);
      setCopy({ kind: 'copied' });
      if (resetTimer.current != null) {
        clearTimeout(resetTimer.current);
      }
      resetTimer.current = setTimeout(
        () => setCopy({ kind: 'idle' }),
        COPIED_FEEDBACK_MS,
      );
    } catch (error) {
      setCopy({ kind: 'error', message: describeError(error) });
    }
  };

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        maxWidth: '100%',
      }}
    >
      {url != null ? (
        <button
          type="button"
          aria-label={t('Preview')}
          onClick={() => onPreview(url)}
          css={{
            display: 'block',
            padding: 0,
            border: `1px solid ${colors.divider}`,
            borderRadius: 8,
            overflow: 'hidden',
            background: colors.grey100,
            cursor: 'zoom-in',
            width: thumbnail.width,
            maxWidth: '100%',
          }}
        >
          <img
            src={url}
            alt={alt}
            width={thumbnail.width}
            height={thumbnail.height}
            loading="lazy"
            onError={() => setBroken(true)}
            css={{
              display: 'block',
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </button>
      ) : (
        <ExpiredChip colors={colors} />
      )}
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 4,
        }}
      >
        {size != null && (
          <Typography variant="caption" sx={{ color: colors.grey600, mr: 0.5 }}>
            {size}
          </Typography>
        )}
        {href != null && (
          <Button
            size="small"
            variant="text"
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            download={visualFilenameOf(image)}
            startIcon={<Download size={14} />}
          >
            {t('Download')}
          </Button>
        )}
        {chatId != null && canCopyImages() && (
          <Button
            size="small"
            variant="text"
            disabled={copy.kind === 'copying'}
            onClick={() => void copyImage()}
            startIcon={
              copy.kind === 'copying' ? (
                <CircularProgress size={12} />
              ) : copy.kind === 'copied' ? (
                <Check size={14} color={colors.green600} />
              ) : (
                <Copy size={14} />
              )
            }
          >
            {copy.kind === 'copied' ? t('Copied') : t('Copy image')}
          </Button>
        )}
      </div>
      {copy.kind === 'error' && (
        <div>
          <Typography
            variant="caption"
            sx={{ display: 'block', color: colors.errorText, mb: 0.5 }}
          >
            {t('Could not copy the image')}
          </Typography>
          <pre css={errorPreCss(colors)}>{copy.message}</pre>
        </div>
      )}
    </div>
  );
};

// crm-chat-api also saved the visual to the user's Files: say so, with the
// Files view's own Preview and Download (fresh signed URL per open), so the
// user knows where to find it once the chat is gone.
const SavedInFiles = ({ artifact }: { artifact: ArtifactSummary }) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const file = useChatFile(artifact);
  const stateText = useFileStateText(file.state);
  const busy = file.state === 'busy';
  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
        padding: '0 10px 10px 10px',
      }}
    >
      <span
        title={artifact.name}
        css={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 8px',
          marginRight: 4,
          borderRadius: 8,
          backgroundColor: colors.green50,
          border: `1px solid ${colors.green200}`,
          color: colors.green700,
          fontSize: '0.75rem',
          lineHeight: 1.4,
        }}
      >
        <FolderCheck size={14} style={{ flexShrink: 0 }} />
        {t('Saved in Files')}
      </span>
      {stateText != null && (
        <Typography variant="caption" sx={{ color: colors.errorText }}>
          {stateText}
        </Typography>
      )}
      {file.state !== 'gone' && (
        <>
          <Button
            size="small"
            variant="text"
            disabled={busy}
            onClick={() => void file.preview()}
            startIcon={<Eye size={14} />}
          >
            {t('Preview')}
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={busy}
            onClick={() => void file.download()}
            startIcon={
              busy ? <CircularProgress size={12} /> : <Download size={14} />
            }
          >
            {t('Download')}
          </Button>
        </>
      )}
      {file.dialogs}
    </div>
  );
};

export const VisualToolCard = ({ part }: { part: ToolPart }) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

  const output = parseVisualToolOutput(part.output);
  const savedArtifact = savedVisualArtifactOf(output);
  const generate = part.toolName === GENERATE_IMAGE_TOOL_NAME;
  const title =
    visualTitleOf(part.input, output) ??
    (generate ? t('Generated an image') : t('Created a visual'));
  const Icon = generate ? WandSparkles : Palette;

  return (
    <div
      css={{
        border: `1px solid ${colors.divider}`,
        borderRadius: 8,
        backgroundColor: colors.white,
        overflow: 'hidden',
      }}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 10px',
          color: colors.grey700,
        }}
      >
        <Icon size={15} color={colors.grey600} style={{ flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, flex: 1, minWidth: 0 }}
          noWrap
        >
          {title}
        </Typography>
        <Check size={14} color={colors.green600} />
      </div>
      {output.modelId != null && (
        <Typography
          variant="caption"
          component="div"
          sx={{ px: '10px', mt: -0.5, mb: 0.75, color: colors.grey600 }}
        >
          {output.modelId}
        </Typography>
      )}
      <div
        css={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          padding: '0 10px 10px 10px',
        }}
      >
        {output.images.map((image, index) => (
          <VisualImageTile
            key={`${index}-${image.imageId}`}
            image={image}
            alt={title}
            onPreview={setPreviewUrl}
          />
        ))}
      </div>
      {savedArtifact != null && <SavedInFiles artifact={savedArtifact} />}
      <ChatImagePreviewDialog
        src={previewUrl}
        alt={title}
        onClose={() => setPreviewUrl(null)}
      />
    </div>
  );
};
